// Imports => React
import { useCallback, useMemo } from "react";
import { withStore } from "@stores";
import { observer } from "mobx-react-lite";
import clsx from "clsx";

// Imports => Constants
import {
  AcIcon,
  AcIsSet,
  AcLoader,
  AcTooltip,
  ICONS,
  KEYS,
  THEMES,
} from "@opp/ui-kit";

// Imports => Atoms
import { AcContainer, AcRow, AcColumn, AcHeading } from "@opp/ui-kit";
import { AcFormatCurrencyAmount } from "../../utilities";
import { withTranslation } from "react-i18next";
import { useGetBalanceQuery } from "@src/api/openapi/merchantApi";

const _CLASSES = {
  MAIN: "ac-balance-breakdown-modal ac-modal--size-md",
  CONTENT: "ac-balance-breakdown-modal__content",
};

const AcBalanceBreakDownModal = ({ store: { ui, balance }, i18n, t }) => {
  const { data: balanceData, isLoading } = useGetBalanceQuery();
  const current_balance = balanceData?.data;

  const handleClose = (event) => {
    if (event && event.preventDefault) {
      event.preventDefault();
    }

    ui.reset(KEYS.MODAL, false);
  };

  const getContentClassNames = useMemo(() => {
    return clsx([_CLASSES.CONTENT]);
  }, []);

  const getStyleClassNames = useMemo(() => {
    return clsx([_CLASSES.MAIN]);
  }, []);

  const getFormatted = useCallback(
    (amount) => {
      if (!AcIsSet(current_balance)) {
        return "";
      }

      balance = AcFormatCurrencyAmount(
        amount,
        current_balance.currency,
        i18n.resolvedLanguage,
        true
      );

      return balance;
    },
    [current_balance, i18n.resolvedLanguage]
  );

  if (isLoading) return <AcLoader loading />;

  return (
    <div className={getStyleClassNames} style={{ overflow: "hidden" }}>
      <div className={getContentClassNames}>
        <AcContainer fluid>
          <AcRow className={"h-margin-y-20"}>
            <AcColumn>
              <AcRow>
                <AcColumn>
                  <span className={"h-text--color-medium"}>
                    {t("balance-breakdown.available.title", {
                      ns: "dashboard",
                    })}
                  </span>
                  <AcTooltip
                    content={t("balance-breakdown.available.tooltip", {
                      ns: "dashboard",
                    })}
                    trigger="hover"
                    className={"d-md-inline d-none h-v-align-middle"}
                  >
                    <span>
                      <AcIcon
                        icon={ICONS.INFO_OUTLINED}
                        className={
                          "h-margin-left-5 h-text--color-medium h-text--size-medium"
                        }
                      />
                    </span>
                  </AcTooltip>
                </AcColumn>
              </AcRow>

              <AcHeading
                className={"h-margin-top-10"}
                rank={5}
                theme={THEMES.PITCH}
              >
                {getFormatted(
                  current_balance?.amount - current_balance?.amount_planned
                )}
              </AcHeading>
            </AcColumn>

            <AcColumn>
              <AcRow>
                <AcColumn>
                  <span className={"h-text--color-medium"}>
                    {t("balance-breakdown.total.title", { ns: "dashboard" })}
                  </span>
                  <AcTooltip
                    content={t("balance-breakdown.total.tooltip", {
                      ns: "dashboard",
                    })}
                    trigger="hover"
                    className={"d-md-inline d-none h-v-align-middle"}
                  >
                    <span>
                      <i
                        className="ac-icon ac-icon--info-outlined h-margin-left-5 h-text--color-medium h-text--size-medium"
                        role="img"
                        alt="icon: info-outlined"
                        title=""
                      ></i>
                    </span>
                  </AcTooltip>
                </AcColumn>
              </AcRow>
              <AcHeading
                className={"h-margin-top-10"}
                rank={5}
                theme={THEMES.PITCH}
              >
                {getFormatted(current_balance.amount)}
              </AcHeading>
            </AcColumn>
          </AcRow>
          <AcRow className={"h-margin-y-20"}>
            <AcColumn>
              <AcRow>
                <AcColumn>
                  <span className={"h-text--color-medium"}>
                    {t("balance-breakdown.reserved.title", { ns: "dashboard" })}
                  </span>
                  <AcTooltip
                    content={t("balance-breakdown.reserved.tooltip", {
                      ns: "dashboard",
                    })}
                    trigger="hover"
                    className={"d-md-inline d-none h-v-align-middle"}
                  >
                    <span>
                      <i
                        className="ac-icon ac-icon--info-outlined h-margin-left-5 h-text--color-medium h-text--size-medium"
                        role="img"
                        alt="icon: info-outlined"
                        title=""
                      ></i>
                    </span>
                  </AcTooltip>
                </AcColumn>
              </AcRow>
              <AcHeading
                className={"h-margin-top-10"}
                rank={5}
                theme={THEMES.PITCH}
              >
                {getFormatted(current_balance.amount_reserved)}
              </AcHeading>
            </AcColumn>

            <AcColumn>
              <AcRow>
                <AcColumn>
                  <span className={"h-text--color-medium"}>
                    {t("balance-breakdown.planned.title", { ns: "dashboard" })}
                  </span>
                  <AcTooltip
                    content={t("balance-breakdown.planned.tooltip", {
                      ns: "dashboard",
                    })}
                    trigger="hover"
                    className={"d-md-inline d-none h-v-align-middle"}
                    style={{ fontSize: "12px;" }}
                  >
                    <span>
                      <i
                        className="ac-icon ac-icon--info-outlined h-margin-left-5 h-text--color-medium h-text--size-medium"
                        role="img"
                        alt="icon: info-outlined"
                        title=""
                      ></i>
                    </span>
                  </AcTooltip>
                </AcColumn>
              </AcRow>
              <AcHeading
                className={"h-margin-top-10"}
                rank={5}
                theme={THEMES.PITCH}
              >
                {getFormatted(current_balance.amount_planned)}
              </AcHeading>
            </AcColumn>
          </AcRow>
        </AcContainer>
      </div>
    </div>
  );
};

export default withTranslation(["dashboard", "common"])(
  withStore(observer(AcBalanceBreakDownModal))
);
